import { create } from "zustand";

/**
 * localStorage key for the per-thread "user acknowledged sending an unanonymized turn"
 * map. Survives reloads so the warning modal does not re-fire once the user explicitly
 * confirmed sending plaintext on a thread that previously had anonymized turns. Shape:
 * `Record<threadId, true>` serialized as JSON. Read once at store init; written on every
 * acknowledgment. Best-effort — localStorage failures fall back to in-memory only.
 */
const UNANONYMIZED_SEND_ACK_KEY = "praetor:anon-send-acknowledged";

function loadAcknowledgedFromStorage(): Record<string, true> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(UNANONYMIZED_SEND_ACK_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      const out: Record<string, true> = {};
      for (const k of Object.keys(parsed)) {
        if (parsed[k] === true) out[k] = true;
      }
      return out;
    }
  } catch {
    // Corrupted entry — ignore and start fresh; the worst-case is the user sees one
    // re-prompt of the warning modal.
  }
  return {};
}

function persistAcknowledged(next: Record<string, true>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(UNANONYMIZED_SEND_ACK_KEY, JSON.stringify(next));
  } catch {
    // Quota or private-mode failures — in-memory state still applies for this session.
  }
}

/**
 * Per-thread anonymization state. Mirrors the backend's persisted column 1:1 — every
 * thread carries a concrete on/off value, no tri-state:
 *  - `enabled === true | false` → the persisted per-thread setting.
 *  - `everAnonymized === true` → at least one prior turn successfully anonymized. Drives the
 *    flip-off warning modal: turning the toggle off when this flag is true means the next
 *    turn sends the thread's prior history in clear, so we ask the user to confirm.
 *
 * The store is keyed by threadId. Entries are populated lazily — when a thread is loaded
 * (customThreadListProvider.fetch) or when the PATCH endpoint returns a fresh snapshot
 * (setThreadAnonymization). Reads from `useAnonymizationStore.getState().byThread[id]`.
 */
export type AnonymizationThreadState = {
  enabled: boolean;
  everAnonymized: boolean;
};

/**
 * Default for a fresh new-thread slot. `enabled: false` matches the project-wide
 * server default (`AnonymizationOptions.DefaultEnabled = false`). The welcome-screen
 * toggle can flip it to `true` before create; whatever value is here at create time
 * is what the backend persists onto the new thread.
 * `everAnonymized: false` because the thread doesn't exist yet — no history to leak.
 */
const NEW_THREAD_INITIAL_ANONYMIZATION: AnonymizationThreadState = {
  enabled: false,
  everAnonymized: false,
};

type AnonymizationState = {
  byThread: Record<string, AnonymizationThreadState>;
  /**
   * Pre-creation slot used by the composer toggle when there's no threadId yet (welcome
   * screen / first-message UI). Persisted onto the new thread at create time via
   * `POST /api/threads`'s `anonymizationEnabled` field, then mirrored into `byThread`
   * once the server returns the new thread id.
   */
  newThread: AnonymizationThreadState;
  /**
   * Per-thread acknowledgment that the user explicitly confirmed sending an unanonymized
   * turn on a thread that previously had anonymized turns (`everAnonymized === true` AND
   * `enabled === false` AND not yet acknowledged → warning modal fires). Persisted to
   * localStorage so the modal does not re-fire across page reloads — survives until the
   * user clears site data or the thread is deleted. Acknowledgment is one-way per thread;
   * no UI exists to revoke it (re-enabling anonymization on the thread mid-conversation
   * naturally suppresses the trigger condition anyway).
   */
  acknowledgedUnanonymizedSend: Record<string, true>;
  set: (threadId: string, next: AnonymizationThreadState) => void;
  clear: (threadId: string) => void;
  setNewThread: (next: AnonymizationThreadState) => void;
  resetNewThread: () => void;
  acknowledgeUnanonymizedSend: (threadId: string) => void;
};

export const useAnonymizationStore = create<AnonymizationState>((set) => ({
  byThread: {},
  newThread: NEW_THREAD_INITIAL_ANONYMIZATION,
  acknowledgedUnanonymizedSend: loadAcknowledgedFromStorage(),
  set: (threadId, next) =>
    set((state) => ({
      byThread: { ...state.byThread, [threadId]: next },
    })),
  clear: (threadId) =>
    set((state) => {
      const { [threadId]: _, ...rest } = state.byThread;
      return { byThread: rest };
    }),
  setNewThread: (next) => set({ newThread: next }),
  resetNewThread: () => set({ newThread: NEW_THREAD_INITIAL_ANONYMIZATION }),
  acknowledgeUnanonymizedSend: (threadId) =>
    set((state) => {
      if (state.acknowledgedUnanonymizedSend[threadId] === true) return state;
      const next = { ...state.acknowledgedUnanonymizedSend, [threadId]: true as const };
      persistAcknowledged(next);
      return { acknowledgedUnanonymizedSend: next };
    }),
}));
